import { ExternalLink } from "lucide-react";

const RATING = 4.8;
const REVIEW_COUNT_TEXT = "Google beoordelingen";
const REVIEWS_URL = "https://www.google.com/maps/search/Used+Car+Selection+Reedonk+Bornem";

const REVIEWS = [
    { naam: "Jan Vermeulen", tekst: "Correcte en zeer goede service! Houdt zich aan zijn woord en doet wat hij belooft.", sterren: 5 },
    { naam: "Sofie Janssens", tekst: "Vriendelijke verkoper en mooi wagenaanbod. Eerlijk en heel klantvriendelijk.", sterren: 5 },
    { naam: "Pieter De Smet", tekst: "Alles ging vlot van begin tot levering. Aanrader!", sterren: 5 },
];

// Big visible star — fully filled, half, or empty
const Star = ({ fill = 1, size = 32 }) => {
    const id = `star-grad-${Math.random().toString(36).slice(2, 8)}`;
    const pct = Math.max(0, Math.min(1, fill)) * 100;
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" className="drop-shadow-sm">
            <defs>
                <linearGradient id={id} x1="0" x2="1" y1="0" y2="0">
                    <stop offset={`${pct}%`} stopColor="#EC6A0A" />
                    <stop offset={`${pct}%`} stopColor="#E5E7EB" />
                </linearGradient>
            </defs>
            <path
                d="M12 2.5l2.95 5.98 6.6.96-4.78 4.66 1.13 6.58L12 17.6l-5.9 3.08 1.13-6.58L2.45 9.44l6.6-.96L12 2.5z"
                fill={`url(#${id})`}
                stroke="#EC6A0A"
                strokeWidth="0.6"
                strokeLinejoin="round"
            />
        </svg>
    );
};

const renderStars = (rating, size = 32) => {
    const out = [];
    for (let i = 0; i < 5; i++) {
        const fill = Math.max(0, Math.min(1, rating - i));
        out.push(<Star key={i} fill={fill} size={size} />);
    }
    return out;
};

const GoogleLogo = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
);

export const ReviewsSection = () => (
    <section className="py-24 bg-[#F8F9FA]" data-testid="reviews-section">
        <div className="ucs-container">
            <div className="grid md:grid-cols-12 gap-10 items-center mb-14">
                <div className="md:col-span-7">
                    <div className="label-eyebrow mb-2">Wat onze klanten zeggen</div>
                    <h2 className="text-3xl md:text-5xl font-black text-[#0E2C4D] tracking-tight">
                        {RATING.toFixed(1).replace(".", ",")} op 5 sterren
                    </h2>
                    <p className="mt-3 text-slate-600 max-w-xl">
                        Onze klanten beoordelen ons gemiddeld {RATING.toFixed(1).replace(".", ",")}/5 op Google.
                        Eerlijke service, correcte communicatie en mooi geselecteerde tweedehands wagens.
                    </p>
                </div>
                <div className="md:col-span-5">
                    <div className="bg-white border border-slate-200 p-7 flex items-center gap-5" style={{borderRadius: 4}} data-testid="reviews-summary">
                        <div>
                            <div className="text-5xl font-black text-[#0E2C4D] leading-none">{RATING.toFixed(1).replace(".", ",")}</div>
                            <div className="flex items-center gap-1 mt-2">{renderStars(RATING, 24)}</div>
                            <div className="mt-2 flex items-center gap-2 text-xs text-slate-500">
                                <GoogleLogo /> {REVIEW_COUNT_TEXT}
                            </div>
                        </div>
                        <div className="flex-1 text-right">
                            <a href={REVIEWS_URL}
                               target="_blank" rel="noopener noreferrer"
                               className="ucs-btn-accent text-sm" data-testid="reviews-source-link">
                                Lees op Google <ExternalLink className="w-4 h-4" />
                            </a>
                        </div>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {REVIEWS.map((r, i) => (
                    <div key={i} className="bg-white border border-slate-200 p-7 relative" style={{borderRadius: 4}} data-testid={`review-card-${i}`}>
                        <div className="absolute top-5 right-5 opacity-90"><GoogleLogo /></div>
                        <div className="flex items-center gap-1 mb-4">{renderStars(r.sterren, 18)}</div>
                        <p className="text-slate-700 leading-relaxed text-sm">"{r.tekst}"</p>
                        <div className="mt-5 pt-5 border-t border-slate-100 text-sm font-bold text-[#0E2C4D]">
                            — {r.naam}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    </section>
);
